import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, FileText } from "lucide-react";
import { formatMonthBR, computePaymentDisplayStatus, STATUS_LABEL } from "@/lib/whatsapp";
import { registeredByMonth } from "@/lib/eligibility";

function brl(v) {
  if (v == null || v === "") return "—";
  return `R$ ${Number(v).toFixed(2).replace(".", ",")}`;
}

export default function FinanceReport({ open, onOpenChange, initialMonth }) {
  const [month, setMonth] = useState(initialMonth);
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => { if (open) setMonth(initialMonth); }, [open, initialMonth]);

  useEffect(() => {
    if (!open || !month) return;
    setLoading(true);
    Promise.all([api.get("/students"), api.get("/payments", { params: { month } })])
      .then(([s, p]) => {
        setStudents(s.data.filter(x => x.status === "ativo"));
        setPayments(p.data);
      })
      .catch(err => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, month]);

  const rows = useMemo(() => {
    const byStudent = Object.fromEntries(payments.map(p => [p.student_id, p]));
    return students
      .filter(s => registeredByMonth(s, month))
      .map((s) => {
      const p = byStudent[s.id];
      const amount = p?.amount != null ? Number(p.amount) : (s.monthly_fee != null ? Number(s.monthly_fee) : null);
      const ds = computePaymentDisplayStatus({ status: p?.status, month });
      return { id: s.id, name: s.name, category: s.category, amount, status: ds };
    }).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [students, payments, month]);

  const totals = useMemo(() => {
    let totalPago = 0, totalAVencer = 0, totalAtrasado = 0, totalPrevisto = 0;
    let countPago = 0, countAVencer = 0, countAtrasado = 0;
    for (const r of rows) {
      if (r.amount != null) totalPrevisto += r.amount;
      if (r.status === "pago") { countPago++; if (r.amount != null) totalPago += r.amount; }
      else if (r.status === "a_vencer") { countAVencer++; if (r.amount != null) totalAVencer += r.amount; }
      else { countAtrasado++; if (r.amount != null) totalAtrasado += r.amount; }
    }
    return { totalPago, totalAVencer, totalAtrasado, totalPrevisto, countPago, countAVencer, countAtrasado };
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="finance-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="size-5 text-[hsl(var(--vv-lime))]" /> Relatório mensal
          </DialogTitle>
          <DialogDescription>Visualize e imprima o resumo financeiro do mês selecionado.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row sm:items-end gap-3 print:hidden">
          <div>
            <Label>Mês do relatório</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="report-month-input" />
          </div>
          <Button
            onClick={() => window.print()}
            className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider ml-auto"
            data-testid="report-print-button"
          >
            <Printer className="size-4 mr-2" /> Imprimir / PDF
          </Button>
        </div>

        <div id="finance-report-print" className="vv-print-area">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground print:text-black">Vôlei Vida — Financeiro</div>
              <h2 className="text-2xl font-black">Relatório de {formatMonthBR(month)}</h2>
            </div>
            <div className="text-right text-xs text-muted-foreground print:text-black">
              Gerado em {new Date().toLocaleDateString("pt-BR")} {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="vv-card p-3">
              <div className="text-[10px] uppercase text-muted-foreground print:text-black">Pagos</div>
              <div className="text-xl font-black text-[hsl(var(--vv-lime))] print:text-black">{totals.countPago}</div>
              <div className="text-xs text-muted-foreground print:text-black">{brl(totals.totalPago)}</div>
            </div>
            <div className="vv-card p-3">
              <div className="text-[10px] uppercase text-muted-foreground print:text-black">A vencer</div>
              <div className="text-xl font-black text-[hsl(var(--vv-yellow))] print:text-black">{totals.countAVencer}</div>
              <div className="text-xs text-muted-foreground print:text-black">{brl(totals.totalAVencer)}</div>
            </div>
            <div className="vv-card p-3">
              <div className="text-[10px] uppercase text-muted-foreground print:text-black">Atrasados</div>
              <div className="text-xl font-black text-[hsl(0_80%_70%)] print:text-black">{totals.countAtrasado}</div>
              <div className="text-xs text-muted-foreground print:text-black">{brl(totals.totalAtrasado)}</div>
            </div>
            <div className="vv-card p-3">
              <div className="text-[10px] uppercase text-muted-foreground print:text-black">Total previsto</div>
              <div className="text-xl font-black print:text-black">{brl(totals.totalPrevisto)}</div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Carregando...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhum aluno ativo no período.</div>
          ) : (
            <table className="w-full text-sm border-collapse" data-testid="report-table">
              <thead>
                <tr className="text-left uppercase text-xs border-b border-border print:border-black">
                  <th className="py-2 pr-3">Aluno</th>
                  <th className="py-2 pr-3">Categoria</th>
                  <th className="py-2 pr-3">Valor</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 print:border-black/40">
                    <td className="py-1.5 pr-3 font-semibold">{r.name}</td>
                    <td className="py-1.5 pr-3">{r.category}</td>
                    <td className="py-1.5 pr-3">{brl(r.amount)}</td>
                    <td className="py-1.5 pr-3 capitalize">
                      <span className={
                        r.status === "pago" ? "text-[hsl(var(--vv-lime))] font-bold print:text-black" :
                        r.status === "a_vencer" ? "text-[hsl(var(--vv-yellow))] font-bold print:text-black" :
                        "text-[hsl(0_80%_70%)] font-bold print:text-black"
                      }>{STATUS_LABEL[r.status]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
